/**
 * HARDLOCK v18.12.71-STABLE-HYDRATION-FIX
 * Archivo critico de Planeacion/Seguimiento.
 * No modificar sin snapshot, pruebas hardlock, localhost validation y revision de no regresion.
 */
import { SectionMetrics, SectionNumber } from './planningTypes';
import { filledFieldCount, isMeaningfulValue, deepFreeze, normalizeStr } from './planningGuards';
import { DYNAMIC_SECTIONS, PLANNING_SECTION_CONTRACT } from './planningConstants';

export type SectionHandler = (content: any, manualGoal: number, sn: number) => SectionMetrics;

/**
 * v18.12.50: Resolución canónica de filas para evitar conteo de metadatos.
 */
const getRowsForSection = (content: any, sn: number): any[] => {
    if (Array.isArray(content)) return content;
    if (!content || typeof content !== 'object') return [];

    const contract = PLANNING_SECTION_CONTRACT[sn as SectionNumber];
    const key = contract?.canonicalKey || `sec${sn}_data`;
    
    if (Array.isArray(content[key])) return content[key];

    // Fallback omnívoro específico (Legacy)
    const aliases: Record<number, string[]> = {
        1: ['sec1_data', 'objetivos_data', 'contribucion_agenda'],
        2: ['sec2_data', 'objetivos_presidente_data', 'plan_estrategico'],
        3: ['sec3_data', 'diagnostico_data'],
        4: ['sec4_data', 'estructura_data'],
        5: ['sec5_data', 'comisiones_data', 'vinculacion'],
        6: ['sec6_data', 'eventos_data'],
        7: ['sec7_data', 'proyectos_data'],
        8: ['convenios_data', 'sec8_data', 'convenios'],
        9: ['sec9_data', 'temas_data']
    };

    const fallbacks = aliases[sn] || [];
    for (const k of fallbacks) {
        if (Array.isArray(content[k])) return content[k];
    }

    return [];
};

/**
 * v18.12.50: Handler Estándar (S1, S2, S4, S6, S7, S9).
 * Meta es manual (desde el Dashboard), Avance es conteo de filas válidas.
 */
const handleTableSection: SectionHandler = (content, manualGoal, sn) => {
    const rows = getRowsForSection(content, sn);
    const progress = rows.filter((r: any) => filledFieldCount(r, sn) >= 1).length;
    return { goal: manualGoal, progress, percentage: 0, isValid: true };
};

/**
 * DO NOT REFACTOR
 * S3 DIAGNOSTIC COUNTS BY BLOCKS ONLY
 * HISTORICAL VALIDATED LOGIC
 */
const handleDiagnosticSection: SectionHandler = (content, manualGoal, sn) => {
    // v18.12.55: Detección omnívora de bloques. 
    // Buscamos en el root y en el contenedor canónico 'sec3_data'
    const rawData = (content && typeof content === 'object') ? content : {};
    const nestedData = rawData.sec3_data || {};
    
    // Helper local para validar contenido real en filas específicas
    const hasRealContent = (rowList: any, fields: string[]) => {
        if (!Array.isArray(rowList)) return false;
        return rowList.some(row => 
            fields.some(f => isMeaningfulValue(row[f]))
        );
    };

    let a = 0; // Temas y Tendencias
    let f = 0, o = 0, d = 0, am = 0; // FODA
    let c = 0; // Intervenciones Previas

    // BLOQUE A — TEMAS Y TENDENCIAS (+1)
    const temasRows = nestedData.temas_sector || rawData.temas_sector || 
                      nestedData.temas_data || rawData.temas_data || 
                      (Array.isArray(content) ? content : []);
    
    const temasFields = ['tema', 'descripcion', 'descripcion_efecto', 'impacto', 'tendencia', 'observaciones', 'riesgo'];
    if (hasRealContent(temasRows, temasFields)) {
        a = 1;
    }

    // BLOQUE B — FODA (Max +4)
    const swot = nestedData.swot_sector || rawData.swot_sector || 
                 nestedData.swot || rawData.swot || 
                 nestedData.foda || rawData.foda || 
                 nestedData.diagnostico || rawData.diagnostico || 
                 (typeof nestedData === 'object' && Object.keys(nestedData).length > 0 ? nestedData : rawData);

    if (swot && typeof swot === 'object' && !Array.isArray(swot)) {
        if (isMeaningfulValue(swot.strengths || swot.fortalezas || swot.f)) f = 1;
        if (isMeaningfulValue(swot.opportunities || swot.oportunidades || swot.o)) o = 1;
        if (isMeaningfulValue(swot.weaknesses || swot.debilidades || swot.d)) d = 1;
        if (isMeaningfulValue(swot.threats || swot.amenazas || swot.a)) am = 1;
    }

    // Fallback rows para FODA si no se encontró en el objeto principal
    if (f + o + d + am === 0 && Array.isArray(temasRows)) {
        if (temasRows.some((r: any) => isMeaningfulValue(r.fortalezas || r.f))) f = 1;
        if (temasRows.some((r: any) => isMeaningfulValue(r.oportunidades || r.o))) o = 1;
        if (temasRows.some((r: any) => isMeaningfulValue(r.debilidades || r.d))) d = 1;
        if (temasRows.some((r: any) => isMeaningfulValue(r.amenazas || r.a))) am = 1;
    }

    // BLOQUE C — INTERVENCIONES PREVIAS (+1)
    const intervRows = nestedData.intervenciones_previas || rawData.intervenciones_previas || 
                       nestedData.intervenciones_data || rawData.intervenciones_data || [];
    
    const intervFields = ['iniciativa', 'resultado', 'periodo', 'descripcion', 'antecedente', 'impacto', 'tema'];
    if (hasRealContent(intervRows, intervFields)) {
        c = 1;
    }

    const total = a + f + o + d + am + c;
    const goal = manualGoal || 0;
    return { goal, progress: total, percentage: 0, isValid: true };
};

/**
 * v18.12.50: Comisiones (S5). 
 * Meta DINÁMICA: Filas con meta_vinculacion 'SI'.
 * Avance: Filas 'SI' con comentarios o estatus.
 */
const handleCommissionsSection: SectionHandler = (content, _manual, sn) => {
    const rows = getRowsForSection(content, sn);
    const instanceName = normalizeStr(content?.nombre_centro || content?.centro || 'UNKNOWN');

    // v18.12.57-S5-AGS-FORENSIC-FIX: Helper local para validación estricta de comentarios
    const isRealComment = (v: any): boolean => {
        if (v === null || v === undefined) return false;
        let s = String(v).trim();
        if (!s || s === "0" || s === "0%") return false;
        
        // Limpieza de HTML para detectar "vacío rico" (<p></p>, &nbsp;, etc.)
        const plain = s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (plain.length === 0) return false;

        // [S5_FORENSIC] Anti-contamination check: 
        // Si el valor es exactamente SI/NO, probablemente es un error de mapeo o placeholder de estatus
        const normalized = normalizeStr(plain);
        if (normalized === 'SI' || normalized === 'NO') return false;

        return isMeaningfulValue(plain);
    };
    
    const isMetaComisionValida = (v: any): boolean => {
        const norm = normalizeStr(v);
        return norm === 'MANTENER' || 
               norm === 'ACTIVAR' || 
               norm === 'CREAR' || 
               norm === 'SI' || 
               norm === 'YES' || 
               norm === 'TRUE';
    };

    // Meta: Únicamente cuando meta_vinculacion o meta es válida según la nueva regla
    const metaRows = rows.filter((r: any) => 
        isMetaComisionValida(r.meta_vinculacion || r.meta)
    );
    const goal = metaRows.length;
    
    // Avance: Únicamente cuando tiene 'SI' Y contenido REAL en el campo canónico
    const progressRows = metaRows.filter((r: any) => {
        // v18.12.59: HARDLOCK CANÓNICO. 
        // Solo 'comentarios_vinculacion' cuenta como avance. Ignoramos 'comentarios', 'acciones' o 'acuerdos' legacy.
        const val = r.comentarios_vinculacion; 
        const hasProgress = isRealComment(val);

        // [S5_FORENSIC] Audit for specific problematic instances like Aguascalientes
        const isAguas = instanceName.includes('AGUASCALIENTES');
        if (isAguas) {
            console.log(`[S5_FORENSIC] instance=${instanceName} rowId=${r.id || r.rowId} meta=${r.meta_vinculacion} rawCanonicVal="${val}" hasProgress=${hasProgress}`);
        }

        return hasProgress;
    });

    const progress = progressRows.length;

    return { goal, progress, percentage: 0, isValid: true };
};

/**
 * v18.12.50: Convenios (S8).
 * Hardlock: Solo cuenta progreso si hay Aliado Y Objeto del convenio.
 */
const handleAgreementsSection: SectionHandler = (content, manualGoal, sn) => {
    const rows = getRowsForSection(content, sn);
    const progress = rows.filter((r: any) => 
        isMeaningfulValue(r.aliado || r.institucion || r.nombre_aliado || r.aliado_institucion) &&
        isMeaningfulValue(r.convenio || r.objetivo || r.descripcion_convenio)
    ).length;
    return { goal: manualGoal, progress, percentage: 0, isValid: true };
};

/**
 * Registry de estrategias de cálculo.
 */
export const SECTION_TYPE_HANDLERS: Record<string, SectionHandler> = deepFreeze({
    'OBJECTIVES': handleTableSection,
    'DIAGNOSTIC': handleDiagnosticSection,
    'PROJECTS': handleTableSection,
    'AGREEMENTS': handleAgreementsSection,
    'COMMISSIONS': handleCommissionsSection,
    'STRUCTURE': handleTableSection,
    'EVENTS': handleTableSection,
    'TOPICS': handleTableSection,
    'DEFAULT': handleTableSection
});

export const getHandler = (type: string): SectionHandler => {
    return SECTION_TYPE_HANDLERS[type] || SECTION_TYPE_HANDLERS['DEFAULT'];
};
